import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import EmptyState from '../../components/EmptyState';
import PermissionGuard from '../../components/PermissionGuard';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import { supplierService, SupplierDTO } from '../../services/supplierService';
import '../../pages/Products/ProductCategoryListPage.css';
import { CirclePlus, Eye, RefreshCw, SquarePen, Trash2, Truck } from 'lucide-react';

const SupplierListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [suppliers, setSuppliers] = useState<SupplierDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSuppliers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSuppliers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await supplierService.getSuppliers({ pageNumber: 1, pageSize: 100 });
      const data = response?.data?.data;
      if (Array.isArray(data)) {
        setSuppliers(data);
      }
      setLoading(false);
    } catch {
      setError(t('supplier.failedToLoadSupplier'));
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await supplierService.getSuppliers({ pageNumber: 1, pageSize: 100 });
      const data = response?.data?.data;
      if (Array.isArray(data)) {
        setSuppliers(data);
      }
    } catch {
      setError(t('supplier.failedToLoadSupplier'));
    } finally {
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'code',
      label: t('supplier.supplierCode'),
      sortable: true,
      width: '130px',
      render: (value) => <code>{value}</code>,
    },
    {
      key: 'name',
      label: t('supplier.supplierName'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'contactEmail',
      label: t('supplier.contactEmail'),
      sortable: true,
      width: '200px',
    },
    {
      key: 'contactPhone',
      label: t('supplier.contactPhone'),
      sortable: false,
      width: '140px',
      hideTablet: true,
      render: (value) => value || 'N/A',
    },
    {
      key: 'city',
      label: t('supplier.city'),
      sortable: true,
      width: '120px',
      hideMobile: true,
      render: (value) => value || 'N/A',
    },
    {
      key: 'country',
      label: t('supplier.country'),
      sortable: true,
      width: '120px',
      hideMobile: true,
      render: (value) => value || 'N/A',
    },
    {
      key: 'isActive',
      label: t('supplier.status'),
      width: '100px',
      hideMobile: true,
      render: (value) => (
        <span className={`badge ${value ? 'badge-active' : 'badge-inactive'}`}>
          {value ? t('supplier.active') : t('supplier.inactive')}
        </span>
      ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item: SupplierDTO) => navigate(`/suppliers/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    ...(hasPermission(Permissions.Suppliers.Edit)
      ? [{
          icon: (
            <SquarePen size={16} strokeWidth={1.75} />
          ),
          onClick: (item: SupplierDTO) => navigate(`/suppliers/${item.id}/edit`),
          className: 'grid-action-btn-edit',
          title: t('common.edit'),
        }]
      : []),
    ...(hasPermission(Permissions.Suppliers.Delete)
      ? [{
          icon: (
            <Trash2 size={16} strokeWidth={1.75} />
          ),
          onClick: (item: SupplierDTO) => navigate(`/suppliers/${item.id}/delete`),
          className: 'grid-action-btn-delete',
          title: t('common.delete'),
        }]
      : []),
  ];

  return (
    <Layout>
      <div className="container">
        <div className="header">
          <h1 className="title">{t('supplier.title')}</h1>
          <div className="header-actions">
            <PermissionGuard claim={Permissions.Suppliers.Create}>
              <button
                className="btn-new"
                onClick={() => navigate('/suppliers/create')}
              >
                <CirclePlus/>
                {t('supplier.newSupplier')}
              </button>
            </PermissionGuard>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('supplier.refreshSuppliers')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('supplier.refreshing') : t('supplier.refreshSuppliers')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={suppliers}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('supplier.noSuppliersFound')}
          emptyState={(
            <EmptyState
              icon={<Truck size={44} strokeWidth={1.2} />}
              title={t('emptyState.suppliers.title') || 'No suppliers yet'}
              description={t('emptyState.suppliers.text')
                || 'Suppliers are who you buy stock from — purchase orders and goods receiving both need one. Add the suppliers you order from.'}
              actionLabel={t('supplier.newSupplier') || 'New Supplier'}
              onAction={() => navigate('/suppliers/create')}
            />
          )}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default SupplierListPage;
